import { useEffect, useState, useCallback } from 'react';

import { PaginationState } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { EntityLoader } from './components/entity-loader';
import { EntityRow } from './components/entity-row';
import { EntityHeader } from './components/entity-table-header';
import { DocumentDownloadOptions, DownloadFormat, StyleFormat } from '@/../../backend/src/types/types';
import { PageName } from '@/analytics/events';
import { TrackingEvents } from '@/analytics/events';
import { trackPageView } from '@/analytics/Mixpanel';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { usePersonsDownload } from '@/api/mutations/useDownloadPersons';
import { useGetCase } from '@/api/queries/useGetCase';
import { useGetPersons } from '@/api/queries/useGetPersonsV2';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { DownloadModal } from '@/new/components/download-modal';
import { Icon } from '@/new/components/icon';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { cn } from '@/new/lib/utils';
import { PersonFiltersV2 } from '@/types';

export function Entities() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const { data: caseData } = useGetCase(matterId);

  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [downloadModalIsOpen, setDownloadModalIsOpen] = useState(false);

  const [filters, setFilters] = useState<PersonFiltersV2>({
    personType: [],
  });

  const { data: entities, isLoading } = useGetPersons({
    caseId: matterId,
    searchQuery,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    paginated: true,
    filters,
  });
  const { mutateAsync: downloadPersons, isLoading: isLoadingDownload } = usePersonsDownload({
    caseId: matterId,
  });

  const handleDownloadCSV = useCallback(
    async ({
      title,
      columns,
      downloadFormat,
    }: {
      title: string;
      columns: string[];
      downloadFormat: DownloadFormat;
    }) => {
      if (downloadFormat === DownloadFormat.EXCEL) {
        trackCustomEvent(TrackingEvents.DownloadPersonsTableCSV, { matterId });
      } else if (downloadFormat === DownloadFormat.DOCX) {
        trackCustomEvent(TrackingEvents.DownloadPersonsTableDocx, { matterId });
      }

      await downloadPersons({ title, columns, styleFormat: StyleFormat.Table, downloadFormat });
      setDownloadModalIsOpen(false);
    },
    [downloadPersons, matterId],
  );

  const handleDownloadWord = useCallback(
    async ({
      title,
      downloadFormat,
      documentOptions,
    }: {
      title: string;
      downloadFormat: DownloadFormat;
      documentOptions: DocumentDownloadOptions;
    }) => {
      trackCustomEvent(TrackingEvents.DownloadPersonsWrittenDocx, { matterId });

      await downloadPersons({ title, styleFormat: StyleFormat.Written, downloadFormat, documentOptions });
      setDownloadModalIsOpen(false);
    },
    [downloadPersons, matterId],
  );

  const onSearchCall = async (query: string) => {
    setSearchQuery(query);
  };

  // Pagination
  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: pagination.pageSize };
    setPagination(newPaginationState);
  };
  const prevPage = () => goToPage(pagination.pageIndex - 1);
  const nextPage = () => goToPage(pagination.pageIndex + 1);

  useEffect(() => {
    trackPageView(PageName.Entities);
  }, []);

  return (
    <div className="flex h-full min-w-full flex-col">
      <Breadcrumb
        items={
          caseData
            ? [
                { href: '/matters', label: 'Matters', active: false },
                { href: `/matters/${matterId}/subsets`, label: caseData.name, active: false },
                { href: `/matters/${matterId}/subsets/${subsetId}`, label: '<subset name>', active: false },
                { href: `/matters/${matterId}/subsets/${subsetId}/entities`, label: 'Entities', active: true },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />
      <EntityHeader
        activeFilters={filters}
        setFilters={setFilters}
        onSearchCall={onSearchCall}
        currentPage={pagination.pageIndex}
        totalPages={entities?.pageCount}
        filterCount={
          Object.values(filters).filter(
            (value) =>
              value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0),
          ).length
        }
        prevPage={prevPage}
        nextPage={nextPage}
        goToPage={goToPage}
        openDownloadModal={() => setDownloadModalIsOpen(true)}
        totalCount={entities?.totalUnfilteredCount}
        filteredCount={entities?.totalCount}
      />

      {isLoading ? (
        <div className="mb-4 h-full w-full overflow-y-auto p-6">
          <div className={cn('flex h-full flex-col gap-6')}>
            {/* Skeleton Loader */}
            {Array.from({ length: 5 }).map((_, index) => (
              <EntityLoader key={index} />
            ))}
          </div>
        </div>
      ) : (
        <div className="mb-4 h-full w-full overflow-y-auto p-6">
          <div className={cn('flex h-full flex-col gap-6')}>
            {entities?.persons.length === 0 && (
              <div className="flex items-center justify-center">
                <Empty className="border border-gray-300 bg-gray-50">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Icon className="iconify lucide--user-round h-5 w-5" />
                    </EmptyMedia>
                    <EmptyTitle>No entities found</EmptyTitle>
                    <EmptyDescription>Sorry, we couldn't find any entities matching these criteria.</EmptyDescription>
                  </EmptyHeader>
                </Empty>
              </div>
            )}

            {entities?.persons.map((entity) => (
              <EntityRow key={entity.id} person={entity} />
            ))}
          </div>
        </div>
      )}

      <DownloadModal
        modalTitle="Export Persons"
        isOpen={downloadModalIsOpen}
        onClose={() => setDownloadModalIsOpen(false)}
        downloadTitle={'Persons'}
        isLoading={isLoadingDownload}
        columnOptions={personsDownloadColumnOptions}
        onDownloadTable={handleDownloadCSV}
        onDownloadWritten={handleDownloadWord}
        displayOptions={{ showStyle: false, showDocumentOptions: true }}
      />
    </div>
  );
}

const personsDownloadColumnOptions = ['Name', 'Type', 'Aliases', 'Description'];
